/**
 * Loading for the two places routes. `?fixtures=1` (dev and preview only) swaps
 * the live read for the samples in `fixtures.ts`.
 *
 * Who sees what: staff (super admin) read every place through the staff door,
 * so brand-owned and house places sit side by side; a brand's staff read every
 * place their brand owns (`Brand.places`, oldest first).
 *
 * One place's page re-reads every 15 s while the tab is visible (`poll.ts`), so
 * the room now stays live. Only those re-reads are timed; first reads are not.
 * The place's seasons are read once per visit and shared: how the last events
 * went, the place's members and its standings fan out from that one read,
 * never polled. The members' lists and the standings after the first season's
 * are read only when the partner opens them. The place's games (its brand's
 * game list, what its rooms start on) are read once per visit, never polled;
 * the catalog once per session per viewer kind (staff or not), forgotten on
 * sign-out; what is playing now comes from the polled place read. The
 * schedule's Next up fans out from the same seasons; its two staff-only groups
 * (repeats, templates) are read only when staff open it, and never for anyone
 * else.
 */
import * as React from "react";
import { useLocation } from "react-router-dom";

import { fetchAllPlaces, fetchBrandPlaces, fetchPlace, type PlaceData, type PlaceListData } from "@/data/places-api";
import {
  fetchEventResults,
  fetchPlaceSeasons,
  fetchSeasonEvents,
  type PlaceSeasonsData,
} from "@/data/place-events-api";
import { fetchSeasonFollowerCount, fetchSeasonFollowers } from "@/data/place-members-api";
import { fetchSeasonStandings } from "@/data/place-standings-api";
import { fetchPlaceGames, readGameCatalogOnce } from "@/data/place-games-api";
import { fetchEventTemplates, fetchScheduledEvents, fetchUpcomingDetails } from "@/data/place-schedule-api";
import { errorMessage } from "@/lib/backend-error";
import { usePortal } from "@/features/portal/portal-context";
import {
  PLACE_EVENT_FIXTURES,
  PLACE_FIXTURES,
  fixtureMembersApi,
  fixtureStaffScheduleApi,
  fixtureStandingsApi,
  isFixtureMode,
  sampleEventsOf,
  sampleGamesOf,
  sampleMemberReadsOf,
  sampleNextUpOf,
  sampleStandingsReadsOf,
} from "@/features/places/fixtures";
import { readPlaceEvents, type PlaceEvents } from "@/features/places/last-events";
import { readPlaceGames, type PlaceGames } from "@/features/places/games";
import {
  placeMembersOf,
  readMemberCounts,
  readMemberLists,
  type MembersApi,
  type PlaceMembers,
  type SeasonFollowersRead,
} from "@/features/places/members";
import { startPolledRead } from "@/features/places/poll";
import {
  placeScheduleOf,
  readNextUp,
  readStaffSchedule,
  type NextUp,
  type PlaceSchedule,
  type ScheduleApi,
  type StaffScheduleApi,
  type StaffState,
} from "@/features/places/schedule";
import {
  placeStandingsOf,
  readFirstStandings,
  readOtherStandings,
  type PlaceStandings,
  type SeasonStandingsRead,
  type StandingsApi,
} from "@/features/places/standings";

export type Load<T> =
  | { state: "loading" }
  | { state: "error"; message: string }
  | {
      state: "ready";
      data: T;
      sample: boolean;
      /** When `data` was read (ms since epoch); null for samples. */
      readAt: number | null;
      /** The latest re-read failed; `data` is the last good read. */
      refreshFailed: boolean;
    };

/** Run `read` (or return `sample`) whenever `deps` change, tracking the state.
 *  One untimed read: a large list (staff read every place, page by page) may
 *  take as long as it needs. `read` gets a signal that aborts when the deps
 *  change or the page goes. Options: `enabled` false holds the read (still
 *  "loading"); `failed` is said when an error has no message of its own.
 *  Returns the setter too, for a poll layered on top. Shared with the rules
 *  presets page (`use-presets.ts`). */
export function useLoad<T>(
  sampleMode: boolean,
  sample: () => T,
  read: (signal: AbortSignal) => Promise<T>,
  deps: React.DependencyList,
  { enabled = true, failed = "Could not read places." }: { enabled?: boolean; failed?: string } = {},
): [Load<T>, React.Dispatch<React.SetStateAction<Load<T>>>] {
  const [load, setLoad] = React.useState<Load<T>>({ state: "loading" });
  React.useEffect(() => {
    if (sampleMode) {
      setLoad({ state: "ready", data: sample(), sample: true, readAt: null, refreshFailed: false });
      return;
    }
    setLoad({ state: "loading" });
    if (!enabled) return;
    let cancelled = false;
    const abort = new AbortController();
    read(abort.signal).then(
      (data) => !cancelled && setLoad({ state: "ready", data, sample: false, readAt: Date.now(), refreshFailed: false }),
      (e: unknown) => !cancelled && setLoad({ state: "error", message: errorMessage(e, failed) }),
    );
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [sampleMode, enabled, ...deps]);
  return [load, setLoad];
}

/** For a read's `.catch`: say the failure on the console (the section says a
 *  plain line; the detail goes here) unless the read was aborted, and fail
 *  on with it. `noun`: what could not be read ("the members"). */
export function warnUnlessAborted(signal: AbortSignal, noun: string): (e: unknown) => never {
  return (e) => {
    if (!signal.aborted) console.warn(`Could not read ${noun}:`, e);
    throw e;
  };
}

/** True when the current location asks for (allowed) fixtures. */
export function useFixtureMode(): boolean {
  return isFixtureMode(useLocation().search);
}

/** The places list on a brand's page. `brandId` comes from the URL. Staff read
 *  every place whichever brand they are under, so their list does not reload
 *  when the brand changes. */
export function usePlaces(brandId: string): Load<PlaceListData[]> {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const [load] = useLoad<PlaceListData[]>(
    sampleMode,
    () => PLACE_FIXTURES,
    async () => {
      if (isSuperAdmin) return fetchAllPlaces({ isSuperAdmin });
      return fetchBrandPlaces(brandId);
    },
    [isSuperAdmin ? null : brandId, isSuperAdmin],
  );
  return load;
}

/** One place by id; `data` is null when it does not exist or is not visible.
 *  After a good first read it re-reads every 15 s while visible (`poll.ts`);
 *  each re-read is aborted after 10 s, and a failed one keeps the last good
 *  numbers on screen with "the last refresh failed, retrying". */
export function usePlace(placeId: string): Load<PlaceData | null> {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const [load, setLoad] = useLoad(
    sampleMode,
    () => PLACE_FIXTURES.find((p) => p.id === placeId) ?? null,
    () => fetchPlace(placeId, { isSuperAdmin }),
    [placeId, isSuperAdmin],
  );
  // Nothing to keep live on samples or on "doesn't exist / no access"; a
  // re-read that finds the place gone stops the poll the same way.
  const live = load.state === "ready" && !load.sample && load.data !== null;
  React.useEffect(() => {
    if (!live) return;
    return startPolledRead({
      read: (signal) => fetchPlace(placeId, { isSuperAdmin }, { signal }),
      onData: (data) => setLoad({ state: "ready", data, sample: false, readAt: Date.now(), refreshFailed: false }),
      onFail: () => setLoad((prev) => (prev.state === "ready" ? { ...prev, refreshFailed: true } : prev)),
    });
  }, [live, placeId, isSuperAdmin]);
  return load;
}

/** A place's seasons, read once per visit and shared by the sections that
 *  fan out from them (how the last events went, the members, the standings),
 *  so they never disagree about which seasons exist. Held until `placeFound`
 *  (no reads for a place that is missing or not visible), and aborted when
 *  the page goes. `data` is null when the place is not visible. */
export function usePlaceSeasons(placeId: string, placeFound: boolean): Load<PlaceSeasonsData | null> {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const [load] = useLoad(
    sampleMode,
    () => PLACE_EVENT_FIXTURES[placeId]?.seasons ?? null,
    (signal) =>
      fetchPlaceSeasons(placeId, { isSuperAdmin }, { signal }).catch(warnUnlessAborted(signal, "the place's seasons")),
    [placeId, isSuperAdmin],
    { enabled: placeFound, failed: "Please try again in a moment." },
  );
  return load;
}

/** The games a place runs (see `games.ts`): its game settings, read once per
 *  visit, then the catalog, read once per session for this viewer kind
 *  (`readGameCatalogOnce`), so moving from place to place does not read it
 *  again (neither is polled: they change only when someone edits them). Held
 *  until `placeFound`; the place's read is aborted when the page goes (the
 *  shared catalog read is not, its answer is just not used). `data` is null
 *  when the place is not visible. */
export function usePlaceGames(placeId: string, placeFound: boolean): Load<PlaceGames | null> {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const [load] = useLoad(
    sampleMode,
    () => sampleGamesOf(placeId, isSuperAdmin),
    (signal) => {
      const viewer = { isSuperAdmin };
      const api = {
        place: (id: string) => fetchPlaceGames(id, viewer, { signal }),
        catalog: () => readGameCatalogOnce(viewer),
      };
      return readPlaceGames(placeId, api).catch(warnUnlessAborted(signal, "the place's games"));
    },
    [placeId, isSuperAdmin],
    { enabled: placeFound, failed: "Please try again in a moment." },
  );
  return load;
}

/** A read that fans out from the shared seasons: held while they load, and
 *  failing with them. */
function useSeasonsLoad<T>(
  seasons: Load<PlaceSeasonsData | null>,
  sample: () => T,
  read: (seasons: PlaceSeasonsData | null, signal: AbortSignal) => Promise<T>,
  deps: React.DependencyList,
): Load<T> {
  const sampleMode = useFixtureMode();
  const data = seasons.state === "ready" ? seasons.data : undefined;
  const [load] = useLoad(sampleMode, sample, (signal) => read(data ?? null, signal), [data, ...deps], {
    enabled: data !== undefined,
    failed: "Please try again in a moment.",
  });
  return seasons.state === "error" && !sampleMode ? seasons : load;
}

/** How a place's last events went, read once (history does not need the
 *  poll; the section says when it was read): see `readPlaceEvents`. Fans out
 *  from the shared seasons read ({@link usePlaceSeasons}). `data` is null when
 *  the place is not visible. */
export function usePlaceEvents(placeId: string, seasons: Load<PlaceSeasonsData | null>): Load<PlaceEvents | null> {
  const { isSuperAdmin } = usePortal();
  return useSeasonsLoad(
    seasons,
    () => sampleEventsOf(placeId),
    (data, signal) => {
      if (!data) return Promise.resolve(null);
      const viewer = { isSuperAdmin };
      return readPlaceEvents(
        placeId,
        {
          seasons: async () => data,
          seasonEvents: (id, offset) => fetchSeasonEvents(id, offset, viewer, { signal }),
          results: (ids) => fetchEventResults(ids, viewer, { signal }),
        },
        Date.now(),
        viewer,
      ).catch(warnUnlessAborted(signal, "the last events"));
    },
    [placeId, isSuperAdmin],
  );
}

/** The members reads with the viewer bound; failures go to the console (the
 *  section says a plain line per season). */
function membersApi(placeId: string, sampleMode: boolean, isSuperAdmin: boolean, signal: AbortSignal): MembersApi {
  if (sampleMode) return fixtureMembersApi(placeId);
  const viewer = { isSuperAdmin };
  const warn = warnUnlessAborted(signal, "the members");
  return {
    followerCount: (id) => fetchSeasonFollowerCount(id, viewer, { signal }).catch(warn),
    followers: (id) => fetchSeasonFollowers(id, viewer, { signal }).catch(warn),
  };
}

type MemberReads = { seasons: PlaceSeasonsData; reads: SeasonFollowersRead[] };

/** A place's members, in two steps (see `members.ts`): each season's count,
 *  read once from the shared seasons, for the People card and the section's
 *  summary; then the lists, only after `showList` is called (the partner
 *  opened the members), once per visit. `data` is null when the place is not
 *  visible. */
export function usePlaceMembers(
  placeId: string,
  seasons: Load<PlaceSeasonsData | null>,
): [Load<PlaceMembers | null>, () => void] {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const counts = useSeasonsLoad<MemberReads | null>(
    seasons,
    () => sampleMemberReadsOf(placeId),
    async (data, signal) => {
      if (!data) return null;
      const api = membersApi(placeId, false, isSuperAdmin, signal);
      return { seasons: data, reads: await readMemberCounts(data, api, Date.now()) };
    },
    [placeId, isSuperAdmin],
  );
  const base = counts.state === "ready" ? counts.data : null;

  // The lists, asked for once per place visit; kept with the counts they
  // extend, so a newer count read never shows an older list.
  const [askedFor, setAskedFor] = React.useState<string | null>(null);
  const [lists, setLists] = React.useState<{ base: MemberReads; reads: SeasonFollowersRead[] | null } | null>(null);
  const asked = askedFor === placeId;
  React.useEffect(() => {
    if (!asked || !base) return;
    setLists({ base, reads: null });
    const abort = new AbortController();
    readMemberLists(base.reads, membersApi(placeId, sampleMode, isSuperAdmin, abort.signal)).then((reads) => {
      if (!abort.signal.aborted) setLists({ base, reads });
    });
    return () => abort.abort();
  }, [asked, base, placeId, sampleMode, isSuperAdmin]);
  const showList = React.useCallback(() => setAskedFor(placeId), [placeId]);

  if (counts.state !== "ready") return [counts, showList];
  if (!base) return [{ ...counts, data: null }, showList];
  const mine = lists?.base === base ? lists : null;
  const reading = asked && (!mine || mine.reads === null);
  return [{ ...counts, data: placeMembersOf(base.seasons, mine?.reads ?? base.reads, reading) }, showList];
}

/** The standings read with the viewer bound; failures go to the console (the
 *  section says a plain line per season). */
function standingsApi(placeId: string, sampleMode: boolean, isSuperAdmin: boolean, signal: AbortSignal): StandingsApi {
  if (sampleMode) return fixtureStandingsApi(placeId);
  const viewer = { isSuperAdmin };
  return {
    standings: (id) => fetchSeasonStandings(id, viewer, { signal }).catch(warnUnlessAborted(signal, "the standings")),
  };
}

type StandingsReads = { seasons: PlaceSeasonsData; reads: SeasonStandingsRead[]; now: number };

/** A place's standings, in two steps (see `standings.ts`): the first season's
 *  table, read once from the shared seasons, for the Leaderboard card and the
 *  section's summary; then the other seasons' tables, only after `showTables`
 *  is called (the partner opened the standings), once per visit. A season
 *  whose read fails is said in its own group and the detail goes to the
 *  console. `data` is null when the place is not visible. */
export function usePlaceStandings(
  placeId: string,
  seasons: Load<PlaceSeasonsData | null>,
): [Load<PlaceStandings | null>, () => void] {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const first = useSeasonsLoad<StandingsReads | null>(
    seasons,
    () => sampleStandingsReadsOf(placeId),
    async (data, signal) => {
      if (!data) return null;
      const now = Date.now();
      return { seasons: data, reads: await readFirstStandings(data, standingsApi(placeId, false, isSuperAdmin, signal), now), now };
    },
    [placeId, isSuperAdmin],
  );
  const base = first.state === "ready" ? first.data : null;

  // The other tables, asked for once per place visit; kept with the first
  // read they extend, so a newer first read never shows older tables.
  const [askedFor, setAskedFor] = React.useState<string | null>(null);
  const [tables, setTables] = React.useState<{ base: StandingsReads; reads: SeasonStandingsRead[] | null } | null>(null);
  const asked = askedFor === placeId;
  React.useEffect(() => {
    if (!asked || !base) return;
    setTables({ base, reads: null });
    const abort = new AbortController();
    readOtherStandings(base.reads, standingsApi(placeId, sampleMode, isSuperAdmin, abort.signal)).then((reads) => {
      if (!abort.signal.aborted) setTables({ base, reads });
    });
    return () => abort.abort();
  }, [asked, base, placeId, sampleMode, isSuperAdmin]);
  const showTables = React.useCallback(() => setAskedFor(placeId), [placeId]);

  if (first.state !== "ready") return [first, showTables];
  if (!base) return [{ ...first, data: null }, showTables];
  const mine = tables?.base === base ? tables : null;
  const reading = asked && (!mine || mine.reads === null);
  return [{ ...first, data: placeStandingsOf(base.seasons, mine?.reads ?? base.reads, base.now, reading) }, showTables];
}

/** The two staff-only reads, through the staff door; failures go to the
 *  console (each group says a plain line). */
function staffScheduleApi(sampleMode: boolean, isSuperAdmin: boolean, signal: AbortSignal): StaffScheduleApi {
  if (sampleMode) return fixtureStaffScheduleApi();
  const viewer = { isSuperAdmin };
  return {
    templates: () => fetchEventTemplates(viewer, { signal }).catch(warnUnlessAborted(signal, "the templates")),
    repeats: () => fetchScheduledEvents(viewer, { signal }).catch(warnUnlessAborted(signal, "the repeats")),
  };
}

type NextUpRead = { seasons: PlaceSeasonsData; nextUp: NextUp };

/** Next up's reads with the viewer bound; a failed details read goes to the
 *  console (`readNextUp` takes it in its stride). */
function nextUpApi(isSuperAdmin: boolean, signal: AbortSignal): ScheduleApi {
  const viewer = { isSuperAdmin };
  return {
    seasonEvents: (id, offset) => fetchSeasonEvents(id, offset, viewer, { signal }),
    details: (ids) =>
      fetchUpcomingDetails(ids, viewer, { signal }).catch(warnUnlessAborted(signal, "the details of what is next up")),
  };
}

/** A place's schedule, in two steps (see `schedule.ts`): Next up, read once
 *  from the shared seasons, for every viewer; then, only after `showStaff` is
 *  called (the section was opened) and only for staff, the repeats and the
 *  templates, once per visit (each further call reads them again: the section
 *  calls it only after one failed). For anyone else `showStaff` asks for nothing.
 *  `rereadNextUp` reads Next up again (after an event is made or undone here),
 *  keeping the rows on screen until the new read lands; a failed re-read keeps
 *  them (`refreshFailed`; the detail on the console). It answers with that
 *  read (one started after the ask), or null when it failed or there was none
 *  to make. `data` is null when the place is not visible. */
export function usePlaceSchedule(
  placeId: string,
  seasons: Load<PlaceSeasonsData | null>,
): [Load<PlaceSchedule | null>, () => void, () => Promise<NextUp | null>] {
  const { isSuperAdmin } = usePortal();
  const sampleMode = useFixtureMode();
  const next = useSeasonsLoad<NextUpRead | null>(
    seasons,
    () => sampleNextUpOf(placeId),
    async (data, signal) => {
      if (!data) return null;
      const nextUp = await readNextUp(data, nextUpApi(isSuperAdmin, signal), Date.now(), { isSuperAdmin }).catch(
        warnUnlessAborted(signal, "what is next up"),
      );
      return { seasons: data, nextUp };
    },
    [placeId, isSuperAdmin],
  );
  const base = next.state === "ready" ? next.data : null;

  // Next up read again, kept with the first read it replaces (so a newer
  // first read never shows an older re-read). Samples are not read again.
  const [rereads, setRereads] = React.useState<{ placeId: string; n: number } | null>(null);
  const [fresh, setFresh] = React.useState<{ base: NextUpRead; nextUp: NextUp; readAt: number } | null>(null);
  const [rereadFailed, setRereadFailed] = React.useState<{ base: NextUpRead; n: number } | null>(null);
  const r = rereads?.placeId === placeId ? rereads.n : 0;
  // Who asked for which re-read (by count): each gets the first read that
  // started at or after its ask, or null when there is none to get.
  const rereadAsks = React.useRef(0);
  const waiters = React.useRef<{ n: number; resolve: (nextUp: NextUp | null) => void }[]>([]);
  const settle = React.useCallback((upTo: number, nextUp: NextUp | null) => {
    const done = waiters.current.filter((w) => w.n <= upTo);
    waiters.current = waiters.current.filter((w) => w.n > upTo);
    for (const w of done) w.resolve(nextUp);
  }, []);
  // The re-read runs once per ask (keyed on the count), on the first read as
  // it is then, so a newer first read never sets off another re-read.
  const baseNow = React.useRef(base);
  // Kept after each commit (before the re-read effect below runs), never mid-render.
  React.useEffect(() => {
    baseNow.current = base;
  });
  React.useEffect(() => {
    const base = baseNow.current;
    // Nothing to read (another place, no read yet, samples): those asking get null.
    if (r === 0 || !base || sampleMode) return settle(r === 0 ? Infinity : r, null);
    const abort = new AbortController();
    readNextUp(base.seasons, nextUpApi(isSuperAdmin, abort.signal), Date.now(), { isSuperAdmin }).then(
      (nextUp) => {
        if (abort.signal.aborted) return;
        setFresh({ base, nextUp, readAt: Date.now() });
        settle(r, nextUp);
      },
      (e: unknown) => {
        if (abort.signal.aborted) return;
        console.warn("Could not read what is next up again:", e);
        setRereadFailed({ base, n: r });
        settle(r, null);
      },
    );
    // A newer ask aborts this read; its read answers this one's askers too.
    return () => abort.abort();
  }, [r, sampleMode, isSuperAdmin, settle]);
  React.useEffect(() => () => settle(Infinity, null), [settle]);
  // The place on screen, so an ask from a place left behind answers null at once.
  const placeNow = React.useRef(placeId);
  React.useEffect(() => {
    placeNow.current = placeId;
  });
  const rereadNextUp = React.useCallback(
    () =>
      new Promise<NextUp | null>((resolve) => {
        if (placeNow.current !== placeId) return resolve(null);
        const n = ++rereadAsks.current;
        waiters.current.push({ n, resolve });
        setRereads({ placeId, n });
      }),
    [placeId],
  );

  // The staff groups, asked for once per place visit (again each time the
  // section asks after one failed). The reads list every template and repeat
  // (not one place's); who belongs here is worked out against the seasons
  // read with Next up.
  const [asks, setAsks] = React.useState<{ placeId: string; n: number } | null>(null);
  const [staff, setStaff] = React.useState<{ placeId: string; state: StaffState } | null>(null);
  const n = asks?.placeId === placeId ? asks.n : 0;
  React.useEffect(() => {
    if (n === 0 || !isSuperAdmin) return;
    setStaff({ placeId, state: "reading" });
    const abort = new AbortController();
    readStaffSchedule({ isSuperAdmin }, staffScheduleApi(sampleMode, isSuperAdmin, abort.signal)).then(
      (read) => {
        if (!abort.signal.aborted && read) setStaff({ placeId, state: read });
      },
      // Each read already fails on its own inside; this is for anything else
      // throwing, so the section says it could not read instead of staying on
      // "reading".
      (e: unknown) => {
        if (abort.signal.aborted) return;
        console.warn("Could not read the staff schedule:", e);
        setStaff({ placeId, state: { templates: "failed", repeats: "failed" } });
      },
    );
    return () => abort.abort();
  }, [n, placeId, sampleMode, isSuperAdmin]);
  const showStaff = React.useCallback(
    () => setAsks((prev) => ({ placeId, n: prev?.placeId === placeId ? prev.n + 1 : 1 })),
    [placeId],
  );

  if (next.state !== "ready") return [next, showStaff, rereadNextUp];
  if (!base) return [{ ...next, data: null }, showStaff, rereadNextUp];
  const state = staff?.placeId === placeId ? staff.state : n > 0 && isSuperAdmin ? "reading" : "unread";
  const mine = fresh?.base === base ? fresh : null;
  return [
    {
      ...next,
      readAt: mine?.readAt ?? next.readAt,
      // The latest re-read (asked for after a write here) failed: the rows are the last good read.
      refreshFailed: rereadFailed?.base === base && rereadFailed.n === r,
      data: placeScheduleOf(placeId, base.seasons, mine?.nextUp ?? base.nextUp, state, { isSuperAdmin }),
    },
    showStaff,
    rereadNextUp,
  ];
}
